import React from 'react';
import styled from 'styled-components';

import { ConditionalLink, Flex, Typography } from 'components';
import { Icon } from 'components/icon';
import { IconsTypes } from 'components/icons';
import { stringfyArrayItems } from 'app/utils';

const CardSiding = styled.div`
  display: none;
  position: absolute;
  width: calc(100% - 2px);
  height: calc(100% - 2px);
  background: ${({ theme }) => theme.colors.black80};
  z-index: 10;
  border-radius: 2px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 1rem;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem;
`;

const TimeIcon = styled(Icon)`
  width: 24px;
`;

const Timer = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 6px;
`;

const TimeWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const BadgesWrapper = styled.div``;

const Badge = styled.div`
  font-size: 0.625rem;
  line-height: 1;
  padding: 4px 6px;
  text-transform: uppercase;
  font-weight: 900;
  border-radius: 4px;
`;

const CardButtonIcon = styled(Icon)`
  display: flex;
  width: 20px;
`;

const CardButtonLabel = styled.div`
  font-size: 1rem;
  line-height: 1;
  font-weight: 700;
  text-transform: uppercase;
`;

const CardButton = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
`;

const Foot = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuestionsCount = styled.div`
  font-size: 0.75rem;
  line-height: 0.875rem;
  font-weight: 700;
`;

const NameWrapper = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
`;

const Name = styled.div`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  margin-top: 8px;
`;

const Wrapper = styled(ConditionalLink)`
  width: 280px;
  height: 340px;
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  background-color: white;

  border: ${(props: any) =>
    props['data-hasimage'] ? '0' : `1px solid ${props['data-theme'] === 'light' ? props.theme.colors.gray_100 : props.theme.colors.dark_150}`};

  border-radius: 4px;

  ${TimeIcon} {
    color: ${(props: any) => (props['data-disabled'] ? props.theme.colors.black30 : props.theme.colors.primary_400)};
  }

  ${Timer} {
    color: ${(props: any) => (props['data-disabled'] ? props.theme.colors.black30 : props.theme.colors.primary_400)};
  }

  ${QuestionsCount} {
    color: ${({ theme }) => theme.colors.black30};
  }

  ${Name} {
    color: ${(props: any) => (props['data-disabled'] ? props.theme.colors.black30 : props.theme.colors.black80)};
  }

  ${Badge} {
    background: ${(props: any) => (props['data-disabled'] ? props.theme.colors.dark_150 : props.theme.colors.alert_400)};
    color: white;
  }

  ${CardButtonIcon} {
    color: ${(props: any) => (props['data-disabled'] ? props.theme.colors.black30 : props.theme.colors.primary_400)};
  }

  ${CardButtonLabel} {
    color: ${(props: any) => (props['data-disabled'] ? props.theme.colors.black30 : props.theme.colors.primary_400)};
  }

  ${CardButton} {
    background: ${(props: any) => props.theme.colors.gray_100};
  }

  &:hover {
    border-color: ${(props: any) =>
      props['data-disabled']
        ? props['data-theme'] === 'light'
          ? props.theme.colors.gray_100
          : props.theme.colors.dark_150
        : props.theme.colors.primary_400};

    ${(props: any) => {
      if (!props['data-disabled']) {
        return `
          box-shadow: 0px 4px 8px ${props.theme.colors.black20};
        `;
      }
    }}

    ${Name} {
      color: ${(props: any) => (props['data-disabled'] ? props.theme.colors.black30 : props.theme.colors.primary_400)};
    }

    ${CardButtonIcon} {
      color: ${(props: any) => (props['data-disabled'] ? props.theme.colors.black30 : props.theme.colors.primary_contrast)};
    }

    ${CardButtonLabel} {
      color: ${(props: any) => (props['data-disabled'] ? props.theme.colors.black30 : props.theme.colors.primary_contrast)};
    }

    ${CardButton} {
      background: ${(props: any) => (props['data-disabled'] ? props.theme.colors.gray_100 : props.theme.colors.primary_400)};
    }

    ${CardSiding} {
      display: flex;
    }
  }
`;

interface ExamProps {
  name: string;
  questionsCount: string;
  duration: string;
  disabled: boolean;
  link?: string;
  badgeLabel?: string;
  buttonLabel: string;
  buttonIcon: IconsTypes;
  allowedCourses: string[];
  theme: 'light' | 'dark';
}

const Exam = ({
  name,
  questionsCount,
  disabled,
  buttonIcon,
  buttonLabel,
  badgeLabel,
  allowedCourses,
  link,
  duration,
  theme = 'light',
}: ExamProps) => {
  return (
    <Wrapper
      component={Flex}
      to={link || ''}
      type={!link ? 'nolink' : 'internal'}
      data-disabled={disabled}
      data-issiding={disabled && !!allowedCourses.length}
      data-theme={theme}
    >
      {disabled && !!allowedCourses.length && (
        <CardSiding>
          <Typography as="span" type="type2" color="white" fontWeight="bold" upper>
            Disponível para:
          </Typography>

          <Typography as="span" type="type1" color="white" lineHeight={1.3} mt={2}>
            {stringfyArrayItems(allowedCourses)}
          </Typography>
        </CardSiding>
      )}

      <Head>
        <TimeWrapper>
          <TimeIcon icon="timerOutline" />
          <Timer>{duration}</Timer>
        </TimeWrapper>

        <BadgesWrapper>{!!badgeLabel && <Badge>{badgeLabel}</Badge>}</BadgesWrapper>
      </Head>

      <Foot>
        <NameWrapper>
          <QuestionsCount>{questionsCount}</QuestionsCount>
          <Name>{name}</Name>
        </NameWrapper>

        <CardButton>
          <CardButtonLabel>{buttonLabel}</CardButtonLabel>
          <CardButtonIcon icon={buttonIcon} />
        </CardButton>
      </Foot>
    </Wrapper>
  );
};

export default Exam;
